import { getSupabaseBrowserClient } from '../lib/supabase'

export type GovernanceStageKey = 'register' | 'assess' | 'control' | 'approve' | 'monitor' | 'review'

export type GovernanceStage = {
  state: string
  sourceRecordIds: string[]
  missing?: string[]
  requiredCount?: number
  blockingCount?: number
  overdueCount?: number
  openConditions?: number
  overdueConditions?: number
  openMaterialChanges?: number
  openIncidents?: number
  nextReviewDate?: string | null
}

export type GovernancePosition = {
  aiUseCaseId: string
  organisationId: string
  operationalState: string
  stages: Record<GovernanceStageKey, GovernanceStage>
  highestPriorityAttentionReason: string | null
  governanceSummary: string
  currentValidApproval: Record<string, unknown> | null
  nextReviewDate: string | null
  reassessmentRequired: boolean
  reapprovalRequired: boolean
}

export type GovernanceRegisterPosition = {
  aiUseCaseId: string
  governanceSummary: string
  highestPriorityAttentionReason: string | null
  stages: Record<GovernanceStageKey, GovernanceStage>
  reassessmentRequired: boolean
  reapprovalRequired: boolean
  nextReviewDate: string | null
}

export type MyAction = {
  sourceType: 'confirmation_requirement' | 'information_request' | 'approval_requirement'
  sourceId: string
  organisationId: string
  aiUseCaseId: string
  aiUseName: string
  actionType: string
  description: string
  requestedBy: string
  relevantDate: string | null
  status: string
  primaryAction: string
  deepLinkPath: string
}

export type MyActionsReadModel = {
  organisationId: string
  actionCount: number
  actions: MyAction[]
  waitingOnOthers: MyAction[]
}

export type ActionsCursor = {
  stateRank: number
  dueAt: string | null
  relevantAt: string
  sourceType: string
  sourceId: string
}

export type ActionsReadModelItem = {
  sourceType: string
  sourceId: string
  aiUseCaseId: string | null
  aiUseName: string | null
  actionType: string
  status: string
  state: string
  assignmentState: string
  assigneeMembershipId: string | null
  assigneeName: string | null
  dueAt: string | null
  relevantAt: string | null
  blocker: string | null
  canOpenDetail: boolean
  deepLinkPath: string | null
}

export type ActionsReadModel = {
  organisationId: string
  view: 'my' | 'organisation'
  totalCount: number
  actionableCount: number
  waitingOnOthersCount: number
  items: ActionsReadModelItem[]
  nextCursor: ActionsCursor | null
}

export type ActionsReadModelQuery = {
  organisationId: string
  view: 'my' | 'organisation'
  actionType?: string | null
  status?: string | null
  overdue?: boolean | null
  cursor?: ActionsCursor | null
  limit?: number
}

export type GovernancePulse = {
  organisationId: string
  visibleAIUseCount: number
  attentionCounts: Record<GovernanceStageKey, number>
}

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}
function objects(value: unknown): Record<string, unknown>[] { return Array.isArray(value) ? value.map(object) : [] }
function strings(value: unknown): string[] { return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [] }
function text(value: unknown): string { return typeof value === 'string' ? value : '' }
function nullableText(value: unknown): string | null { return typeof value === 'string' && value ? value : null }
function numberValue(value: unknown): number | undefined { return typeof value === 'number' ? value : undefined }
function count(value: unknown): number { return typeof value === 'number' ? value : Number(value) || 0 }
function booleanValue(value: unknown): boolean { return value === true || value === 'true' }

function stage(value: unknown): GovernanceStage {
  const row = object(value)
  return {
    state: text(row.state),
    sourceRecordIds: strings(row.sourceRecordIds),
    missing: row.missing ? strings(row.missing) : undefined,
    requiredCount: numberValue(row.requiredCount),
    blockingCount: numberValue(row.blockingCount),
    overdueCount: numberValue(row.overdueCount),
    openConditions: numberValue(row.openConditions),
    overdueConditions: numberValue(row.overdueConditions),
    openMaterialChanges: numberValue(row.openMaterialChanges),
    openIncidents: numberValue(row.openIncidents),
    nextReviewDate: nullableText(row.nextReviewDate),
  }
}

function stages(value: unknown): Record<GovernanceStageKey, GovernanceStage> {
  const row = object(value)
  return {
    register: stage(row.register),
    assess: stage(row.assess),
    control: stage(row.control),
    approve: stage(row.approve),
    monitor: stage(row.monitor),
    review: stage(row.review),
  }
}

async function invoke(functionName: string, args: Record<string, unknown>) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) throw new Error(error.message)
  return data
}

export async function getGovernancePosition(aiUseCaseId: string): Promise<GovernancePosition> {
  const row = object(await invoke('get_phase3_governance_position', { p_ai_use_case_id: aiUseCaseId }))
  return {
    aiUseCaseId: text(row.aiUseCaseId),
    organisationId: text(row.organisationId),
    operationalState: text(row.operationalState),
    stages: stages(row.stages),
    highestPriorityAttentionReason: nullableText(row.highestPriorityAttentionReason),
    governanceSummary: text(row.governanceSummary),
    currentValidApproval: row.currentValidApproval ? object(row.currentValidApproval) : null,
    nextReviewDate: nullableText(row.nextReviewDate),
    reassessmentRequired: booleanValue(row.reassessmentRequired),
    reapprovalRequired: booleanValue(row.reapprovalRequired),
  }
}

export async function getGovernancePulse(organisationId: string): Promise<GovernancePulse> {
  const row = object(await invoke('get_phase3_governance_pulse', { p_organisation_id: organisationId }))
  const attention = object(row.attentionCounts)
  return {
    organisationId: text(row.organisationId),
    visibleAIUseCount: count(row.visibleAIUseCount),
    attentionCounts: {
      register: count(attention.register),
      assess: count(attention.assess),
      control: count(attention.control),
      approve: count(attention.approve),
      monitor: count(attention.monitor),
      review: count(attention.review),
    },
  }
}

export async function listGovernanceRegisterPositions(organisationId: string): Promise<GovernanceRegisterPosition[]> {
  return objects(await invoke('list_phase3_governance_register_positions', { p_organisation_id: organisationId })).map((row) => ({
    aiUseCaseId: text(row.aiUseCaseId),
    governanceSummary: text(row.governanceSummary),
    highestPriorityAttentionReason: nullableText(row.highestPriorityAttentionReason),
    stages: stages(row.stages),
    reassessmentRequired: booleanValue(row.reassessmentRequired),
    reapprovalRequired: booleanValue(row.reapprovalRequired),
    nextReviewDate: nullableText(row.nextReviewDate),
  }))
}

function myAction(value: unknown): MyAction {
  const row = object(value)
  const sourceType = text(row.sourceType)
  return {
    sourceType: sourceType === 'information_request' || sourceType === 'approval_requirement'
      ? sourceType
      : 'confirmation_requirement',
    sourceId: text(row.sourceId),
    organisationId: text(row.organisationId),
    aiUseCaseId: text(row.aiUseCaseId),
    aiUseName: text(row.aiUseName),
    actionType: text(row.actionType),
    description: text(row.description),
    requestedBy: text(row.requestedBy),
    relevantDate: nullableText(row.relevantDate),
    status: text(row.status),
    primaryAction: text(row.primaryAction),
    deepLinkPath: text(row.deepLinkPath),
  }
}

export async function getMyActions(organisationId: string): Promise<MyActionsReadModel> {
  const row = object(await invoke('get_r1_my_actions', { p_organisation_id: organisationId }))
  return {
    organisationId: text(row.organisationId),
    actionCount: count(row.actionCount),
    actions: (Array.isArray(row.actions) ? row.actions : []).map(myAction),
    waitingOnOthers: (Array.isArray(row.waitingOnOthers) ? row.waitingOnOthers : []).map(myAction),
  }
}

function actionCursor(value: unknown): ActionsCursor | null {
  const row = object(value)
  const stateRank = count(row.stateRank)
  const relevantAt = nullableText(row.relevantAt)
  const sourceType = nullableText(row.sourceType)
  const sourceId = nullableText(row.sourceId)
  if (!stateRank || !relevantAt || !sourceType || !sourceId) return null
  return {
    stateRank,
    dueAt: nullableText(row.dueAt),
    relevantAt,
    sourceType,
    sourceId,
  }
}

function actionsReadModelItem(value: unknown): ActionsReadModelItem {
  const row = object(value)
  return {
    sourceType: text(row.sourceType),
    sourceId: text(row.sourceId),
    aiUseCaseId: nullableText(row.aiUseCaseId),
    aiUseName: nullableText(row.aiUseName),
    actionType: text(row.actionType),
    status: text(row.status),
    state: text(row.state),
    assignmentState: text(row.assignmentState),
    assigneeMembershipId: nullableText(row.assigneeMembershipId),
    assigneeName: nullableText(row.assigneeName),
    dueAt: nullableText(row.dueAt),
    relevantAt: nullableText(row.relevantAt),
    blocker: nullableText(row.blocker),
    canOpenDetail: booleanValue(row.canOpenDetail),
    deepLinkPath: nullableText(row.deepLinkPath),
  }
}

export async function getActionsReadModel(query: ActionsReadModelQuery): Promise<ActionsReadModel> {
  const row = object(await invoke('get_r1_actions_read_model', {
    p_organisation_id: query.organisationId,
    p_view: query.view,
    p_action_type: query.actionType ?? null,
    p_status: query.status ?? null,
    p_overdue: query.overdue ?? null,
    p_cursor: query.cursor ?? null,
    p_limit: query.limit ?? 25,
  }))
  const view = text(row.view)
  return {
    organisationId: text(row.organisationId),
    view: view === 'organisation' ? 'organisation' : 'my',
    totalCount: count(row.totalCount),
    actionableCount: count(row.actionableCount),
    waitingOnOthersCount: count(row.waitingOnOthersCount),
    items: (Array.isArray(row.items) ? row.items : []).map(actionsReadModelItem),
    nextCursor: actionCursor(row.nextCursor),
  }
}
